import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from "@tanstack/react-query";
import { trackPayEvent } from "../../../../../../analytics/track/pay.js";
import { NATIVE_TOKEN_ADDRESS } from "../../../../../../constants/addresses.js";
import { getContract } from "../../../../../../contract/contract.js";
import { decimals } from "../../../../../../extensions/erc20/read/decimals.js";
import { shortenAddress } from "../../../../../../utils/address.js";
import { formatNumber } from "../../../../../../utils/formatNumber.js";
import { toTokens } from "../../../../../../utils/units.js";
import { useCustomTheme } from "../../../../../core/design-system/CustomThemeProvider.js";
import { iconSize, spacing } from "../../../../../core/design-system/index.js";
import { useChainMetadata } from "../../../../../core/hooks/others/useChainQuery.js";
import { useActiveWallet } from "../../../../../core/hooks/wallets/useActiveWallet.js";
import { useEnsName } from "../../../../../core/utils/wallet.js";
import { LoadingScreen } from "../../../../wallets/shared/LoadingScreen.js";
import { ChainIcon } from "../../../components/ChainIcon.js";
import { Img } from "../../../components/Img.js";
import { Spacer } from "../../../components/Spacer.js";
import { TokenIcon } from "../../../components/TokenIcon.js";
import { WalletImage } from "../../../components/WalletImage.js";
import { Container, Line, ModalHeader } from "../../../components/basic.js";
import { Button } from "../../../components/buttons.js";
import { Text } from "../../../components/text.js";
import { ConnectButton } from "../../ConnectButton.js";
import { isNativeToken } from "../nativeToken.js";
export function DirectPaymentModeScreen(props) {
    const { payUiOptions, supportedDestinations, client, onContinue, payerAccount, } = props;
    const theme = useCustomTheme();
    const activeWallet = useActiveWallet();
    const metadata = payUiOptions.metadata;
    const paymentInfo = payUiOptions.paymentInfo;
    const { data: chainData } = useChainMetadata(paymentInfo.chain);
    const { data: sellerEns } = useEnsName({
        client,
        address: paymentInfo.sellerAddress,
    });
    const totalCostQuery = useQuery({
        queryKey: ["amount", paymentInfo],
        queryFn: async () => {
            let tokenDecimals = 18;
            if (paymentInfo.token && !isNativeToken(paymentInfo.token)) {
                tokenDecimals = await decimals({
                    contract: getContract({
                        address: paymentInfo.token.address,
                        chain: paymentInfo.chain,
                        client,
                    }),
                });
            }
            let cost;
            if ("amountWei" in paymentInfo) {
                cost = toTokens(paymentInfo.amountWei, tokenDecimals);
            }
            else {
                cost = paymentInfo.amount;
            }
            return cost;
        },
    });
    const totalCost = totalCostQuery.data;
    if (!chainData || totalCost === undefined) {
        return _jsx(LoadingScreen, {});
    }
    const token = paymentInfo.token
        ? {
            ...paymentInfo.token,
            icon: paymentInfo.token?.icon ||
                supportedDestinations
                    .find((c) => c.chain.id === paymentInfo.chain.id)
                    ?.tokens.find((t) => t.address.toLowerCase() ===
                    paymentInfo.token?.address.toLowerCase())?.icon,
        }
        : {
            address: NATIVE_TOKEN_ADDRESS,
            name: chainData.nativeCurrency.name,
            symbol: chainData.nativeCurrency.symbol,
            icon: chainData.icon?.url,
        };
    return (_jsxs(Container, { p: "lg", children: [_jsx(ModalHeader, { title: metadata?.name || "Payment Details" }), _jsx(Spacer, { y: "lg" }), _jsxs(Container, { children: [metadata?.image ? (_jsx(Img, { client: client, src: metadata?.image, style: {
                            width: "100%",
                            borderRadius: spacing.md,
                            backgroundColor: theme.colors.tertiaryBg,
                        } })) : activeWallet ? (_jsxs(Container, { flex: "row", center: "both", style: {
                            padding: spacing.md,
                            marginBottom: spacing.md,
                            borderRadius: spacing.md,
                            backgroundColor: theme.colors.tertiaryBg,
                        }, children: [_jsx(WalletImage, { size: iconSize.xl, id: activeWallet.id, client: client }), _jsx("div", { style: {
                                    flexGrow: 1,
                                    borderBottom: "6px dotted",
                                    borderColor: theme.colors.secondaryIconColor,
                                    marginLeft: spacing.md,
                                    marginRight: spacing.md,
                                } }), _jsx(ChainIcon, { client: client, size: iconSize.xl, chainIconUrl: chainData.icon?.url })] })) : null, _jsx(Spacer, { y: "md" }), _jsxs(Container, { flex: "row", children: [_jsx(Container, { flex: "column", expand: true, children: _jsx(Text, { size: "md", color: "primaryText", weight: 700, children: "Price" }) }), _jsx(Container, { expand: true, children: _jsxs(Container, { flex: "row", gap: "xs", center: "y", style: { justifyContent: "right" }, children: [_jsx(TokenIcon, { chain: paymentInfo.chain, client: props.client, size: "sm", token: token }), _jsxs(Text, { color: "primaryText", size: "md", weight: 700, children: [String(formatNumber(Number(totalCost), 6)), " ", token.symbol] })] }) })] }), _jsx(Spacer, { y: "md" }), _jsx(Line, {}), _jsx(Spacer, { y: "md" }), _jsxs(Container, { flex: "row", children: [_jsx(Container, { flex: "column", expand: true, children: _jsx(Text, { size: "xs", color: "secondaryText", children: "Network" }) }), _jsx(Container, { expand: true, children: _jsxs(Container, { flex: "row", gap: "xs", center: "y", style: { justifyContent: "right" }, children: [_jsx(ChainIcon, { chainIconUrl: chainData.icon?.url, size: "xs", client: props.client }), _jsx(Text, { size: "xs", color: "secondaryText", style: { textAlign: "right" }, children: chainData.name })] }) })] }), _jsx(Spacer, { y: "sm" }), _jsxs(Container, { flex: "row", children: [_jsx(Container, { flex: "column", expand: true, children: _jsx(Text, { size: "xs", color: "secondaryText", children: "Seller" }) }), _jsx(Container, { expand: true, children: _jsx(Container, { flex: "row", gap: "xs", center: "y", style: { justifyContent: "right" }, children: _jsx(Text, { size: "xs", color: "secondaryText", style: { textAlign: "right" }, children: sellerEns || shortenAddress(paymentInfo.sellerAddress) }) }) })] })] }), _jsx(Spacer, { y: "xl" }), payerAccount ? (_jsx(Button, { variant: "accent", fullWidth: true, onClick: () => {
                    trackPayEvent({
                        event: "choose_payment_method_direct_payment_mode",
                        client,
                        walletAddress: payerAccount.address,
                        walletType: activeWallet?.id,
                    });
                    onContinue(totalCost, paymentInfo.chain, token);
                }, children: "Choose Payment Method" })) : (_jsx("div", { children: _jsx(ConnectButton, { ...props.connectOptions, client: client, theme: theme, connectButton: {
                        style: {
                            width: "100%",
                        },
                    } }) }))] }));
}
//# sourceMappingURL=DirectPaymentModeScreen.js.map